import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import RequisitionHeader from "./Components/RequisitionHeader";
import { REQUISITION_STATUSES, PRIORITY_LEVELS } from "../../../constants/inventory";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TablePagination, Checkbox, IconButton, Snackbar, Alert,
  Skeleton,
} from "@mui/material";
import { MdDelete, MdVisibility } from "react-icons/md";

const Requisition = () => {
  const { token, user } = useAuthStore();
  const navigate = useNavigate();
  const [requisitions, setRequisitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const isManager = ["Owner", "Accounts", "StoreIncharge", "Manager"].includes(user?.role);

  const fetchRequisitions = async () => {
    setLoading(true);
    try {
      let url = `/api/getAllRequisitions?page=${page + 1}&limit=${rowsPerPage}`;
      if (statusFilter) url += `&status=${statusFilter}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setRequisitions(data.requisitions || []);
        setTotalCount(data.totalCount || 0);
      }
    } catch (err) {
      console.error("Error fetching requisitions:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequisitions();
  }, [page, rowsPerPage, statusFilter]);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(requisitions.map((r) => r._id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const handleDelete = async () => {
    if (selected.length === 0) return;
    try {
      const response = await fetch("/api/deleteRequisition", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids: selected }),
      });
      const data = await response.json();
      if (response.ok) {
        setSnackbar({ open: true, message: `${data.deletedCount} requisition(s) deleted`, severity: "success" });
        setSelected([]);
        fetchRequisitions();
      } else {
        setSnackbar({ open: true, message: data.error, severity: "error" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to delete", severity: "error" });
    }
  };

  const getStatusBadge = (status) => {
    const config = REQUISITION_STATUSES[status] || { label: status, color: "bg-gray-100 text-gray-800" };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const config = PRIORITY_LEVELS.find((p) => p.id === priority);
    if (!config) return priority;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.name}
      </span>
    );
  };

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      <RequisitionHeader title={isManager ? "All Requisitions" : "My Requisitions"} />

      {/* Filters */}
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All Statuses</option>
          {Object.entries(REQUISITION_STATUSES).map(([key, val]) => (
            <option key={key} value={key}>{val.label}</option>
          ))}
        </select>

        {selected.length > 0 && (
          <button
            onClick={handleDelete}
            className="flex items-center gap-1 bg-red-500 text-white px-3 py-2 rounded-lg text-sm hover:bg-red-600"
          >
            <MdDelete /> Delete ({selected.length})
          </button>
        )}
      </div>

      {/* Table */}
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < requisitions.length}
                    checked={requisitions.length > 0 && selected.length === requisitions.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Req #</TableCell>
                {isManager && <TableCell sx={{ fontWeight: 600 }}>Requested By</TableCell>}
                <TableCell sx={{ fontWeight: 600 }}>Items</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Est. Cost (Nu)</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(isManager ? 9 : 8)].map((_, j) => (
                      <TableCell key={j}><Skeleton animation="wave" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : requisitions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isManager ? 9 : 8} align="center" sx={{ py: 4 }}>
                    <p className="text-gray-400">No requisitions found</p>
                  </TableCell>
                </TableRow>
              ) : (
                requisitions.map((req) => (
                  <TableRow
                    key={req._id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/Requisitions/View/${req._id}`)}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selected.includes(req._id)}
                        onChange={() => handleSelect(req._id)}
                      />
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-primary">{req.requisitionNumber}</span>
                    </TableCell>
                    {isManager && (
                      <TableCell>{req.requestedBy?.name || "N/A"}</TableCell>
                    )}
                    <TableCell>
                      {req.items?.length || 0} item(s)
                    </TableCell>
                    <TableCell>{getPriorityBadge(req.priority)}</TableCell>
                    <TableCell>
                      {req.totalEstimatedCost?.toLocaleString() || "0"}
                    </TableCell>
                    <TableCell>{getStatusBadge(req.status)}</TableCell>
                    <TableCell>
                      {new Date(req.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/Requisitions/View/${req._id}`)}
                      >
                        <MdVisibility className="text-primary" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Requisition;
